import { useContext, useRef } from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';

import { Deal } from '../../../../gql/dealGql';
import { FinancialInfo } from '../../../../gql/financialInfoGql';
import { useResetProcessorAssignmentMutation } from '../../../../gql/generated/graphql';

import { PermissionEnum } from '../../../../constants/permissions';
import { DealActionsEnum, DealContext } from '../../../../libs/DealContext';
import { logger } from '../../../../libs/Logger';
import { AbilityContext } from '../../../../libs/contextLib';

// Lets title clerks (and super users) clear a deal's title-processor transaction so it
// can be re-pushed to a different processor. Only relevant once a deal has been sent.
export const ResetProcessorButton = () => {
  const { deal, dispatch } = useContext(DealContext);
  const abilities = useContext(AbilityContext);
  const { values, setFieldValue } = useFormikContext<Deal>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [resetProcessorAssignment, { loading }] = useResetProcessorAssignmentMutation();

  const canReset =
    abilities.has(PermissionEnum.TitleClerk) || abilities.has(PermissionEnum.SuperUser);
  const alreadySent = !!values.financial_info.tt_transaction_id;

  if (!canReset || !alreadySent) {
    return null;
  }

  const handleReset = async () => {
    if (values.id == null || values.financial_info.id == null) {
      return;
    }

    try {
      const { data } = await resetProcessorAssignment({
        variables: { dealId: values.id, fiId: values.financial_info.id },
      });

      if (!data?.resetProcessorAssignment) {
        toast.error('Failed to reset processor assignment');
        return;
      }

      // Clearing tt_transaction_id re-enables the Send button (which gates on it).
      setFieldValue('financial_info.tt_transaction_id', null);
      dispatch({
        type: DealActionsEnum.UpdateDeal,
        payload: {
          financial_info: {
            ...deal.financial_info,
            ...(data.resetProcessorAssignment as Partial<FinancialInfo>),
          },
        },
      });
      toast.success('Processor assignment reset — you can re-send to a processor');
    } catch (error) {
      toast.error('Failed to reset processor assignment');
      logger.error('ResetProcessorButton.tsx', 'Failed to reset processor assignment', null, error);
    } finally {
      onClose();
    }
  };

  return (
    <>
      <Button variant="warning" size="sm" onClick={onOpen} isLoading={loading}>
        RESET PROCESSOR
      </Button>
      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reset processor assignment
            </AlertDialogHeader>
            <AlertDialogBody>
              This clears the current title-processor transaction so the deal can be re-sent to a
              different processor. Continue?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleReset} isLoading={loading} ml={3}>
                Reset
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
